/**
 * VolumeFreeGiftForm
 * ------------------
 * Volume-Discount-only Free Gift editor, fully bound to the bundle-editor
 * RHF form. This is a fork of components/FreeGift/FreeGiftVolume.tsx, which
 * is shared with Mix & Match (not RHF-based) and therefore can't safely use
 * Form* wrappers everywhere. This copy only ever renders inside the Volume
 * Discount FormProvider, so every field is safely wrapped.
 */

import { useContext, useEffect, useState } from "react";
import styles from "./VolumeContentForm.module.css";
import {
    ActionList,
    Button,
    Icon,
    IconSource,
    InlineStack,
    Popover,
    Text,
    TextField,
    Thumbnail,
    Tooltip,
} from "@shopify/polaris";
import { CollectionIcon, SearchIcon, DeleteIcon } from "@shopify/polaris-icons";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { ProductObj, VariantsObj } from "../../Types";
import CloseIcon from "../../../../components/Icons/CloseIcon";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { getProductsDynamicallyAndReturnNewProductsArray } from "../../constants";
import { FormCheckbox, FormRadioButton, FormTextField } from "../../../../components/FormFields";

type VolumeFreeGiftFormProps = {
    handleInputChange: (index: number, field: any, value: string | number | boolean | ProductObj[]) => void;
    free_gifts: ProductObj[];
    allow_free_gifts: boolean;
    free_gift_text: string;
    selectedTab: number;
    excludeProducts: ProductObj[];
    free_gift_template?: "old" | "modern";
    free_gift_show_price?: boolean;
    handleMultipleInputChanges: (index: number, updates: Record<string, any>) => void;
    offerError: string;
};

const VolumeFreeGiftForm = ({
    handleInputChange,
    allow_free_gifts,
    free_gifts,
    selectedTab,
    free_gift_text,
    excludeProducts,
    free_gift_template,
    free_gift_show_price,
    handleMultipleInputChanges,
    offerError,
}: VolumeFreeGiftFormProps) => {
    const [productSearchQuery, setProductSearchQuery] = useState("");
    const [textVariablePopoverActive, setTextVariablePopoverActive] = useState(false);

    const context = useContext(AppConfigContext);
    const { sft } = context;

    const handleVariableInsert = (variable: string) => {
        handleInputChange(selectedTab, "free_gift_text", `${free_gift_text || ""} ${variable}`);
        setTextVariablePopoverActive(false);
    };

    const handleProductSearch = async () => {
        const preSelected = free_gifts?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({
                id: variant?.id,
            })),
        }));
        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: free_gift_template == "modern" ? 1 : true,
            query: productSearchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                if (!excludeProducts.find((ep) => ep.id == product?.id)) {
                    productsArr.push({
                        id: product?.id,
                        images: product?.images,
                        title: product?.title,
                        handle: product?.handle,
                        quantity: 1,
                        maxProductStock: product?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        isSubscribed: false,
                        variants: product?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,
                            title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: product?.options,
                    });
                } else {
                    shopify.toast.show("Included product cannot be added as free gift(s)", { isError: true });
                }
            });

            setProductSearchQuery("");

            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(productsArr, sft);
                handleInputChange(selectedTab, "free_gifts", newProductsArr);
            } catch (error) {
                console._log(error);
                handleInputChange(selectedTab, "free_gifts", productsArr);
            }
        }
    };

    const removeProducts = (pId: string = "") => {
        const temp = [...free_gifts];
        const idx = temp.findIndex((p: any) => p.id == pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            handleInputChange(selectedTab, "free_gifts", temp);
        }
    };

    const handleQuantity = (pId = "", val: number) => {
        const temp: ProductObj[] = [...free_gifts];
        const idx = temp.findIndex((p) => p.id == pId);

        if (idx >= 0) {
            temp[idx] = {
                ...temp[idx],
                quantity: val,
            };
        }

        handleInputChange(selectedTab, "free_gifts", temp);
    };

    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [free_gifts, allow_free_gifts]);

    return (
        <div
            className={styles.gift_container}
            style={{
                position: "relative",
            }}
        >
            <span
                style={{
                    position: "absolute",
                    right: "5px",
                    top: "5px",
                    cursor: "pointer",
                }}
                onClick={() => handleInputChange(selectedTab, "allow_free_gifts", false)}
            >
                <Icon source={DeleteIcon as IconSource} tone="critical" accessibilityLabel="Delete" />
            </span>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                }}
            >
                <FormRadioButton
                    label={"Multiple Free Gifts"}
                    name={`volume_discounts.${selectedTab}.free_gift_template`}
                    value="old"
                    onChangeHook={() => {
                        handleMultipleInputChanges(selectedTab, {
                            free_gift_template: "old",
                            free_gifts: [],
                            free_gift_show_price: true,
                        });
                    }}
                />
                <FormRadioButton
                    label={"Single Free Gift"}
                    name={`volume_discounts.${selectedTab}.free_gift_template`}
                    value="modern"
                    onChangeHook={() => {
                        handleMultipleInputChanges(selectedTab, {
                            free_gift_template: "modern",
                            free_gifts: [],
                            free_gift_show_price: true,
                        });
                    }}
                />
            </div>

            <h5>Free Gift</h5>
            <TextField
                label={"Search Product"}
                labelHidden
                placeholder="Search Products"
                autoComplete={"off"}
                value={productSearchQuery}
                onChange={(val) => {
                    setProductSearchQuery(val);
                }}
                prefix={<Icon source={SearchIcon} />}
                size="slim"
                connectedRight={
                    <div style={{ marginLeft: "8px" }}>
                        <Button onClick={() => handleProductSearch()} size="slim">
                            Browse
                        </Button>
                    </div>
                }
                error={offerError}
            />

            {free_gifts?.length > 0 && (
                <div className={styles.renderFreeGiftFormSide}>
                    {free_gifts?.map((p: any, index: number) => (
                        <div className={styles.container} key={p.id}>
                            <div className={styles.container_wrapper}>
                                <div className={styles.img_container}>
                                    <Thumbnail
                                        source={p?.images[0]?.originalSrc || CollectionIcon}
                                        alt={p?.title}
                                        size={"small"}
                                    />
                                </div>
                                <p>{p?.title}</p>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center",
                                    maxWidth: "130px",
                                    width: "130px",
                                }}
                            >
                                <FormTextField
                                    name={`volume_discounts.${selectedTab}.free_gifts.${index}.quantity`}
                                    label={"Quantity"}
                                    labelHidden
                                    type={"number"}
                                    size="slim"
                                    min={1}
                                    placeholder={"Quantity"}
                                    autoComplete={"off"}
                                    value={p.quantity.toString()}
                                    suffix={"Qty"}
                                    onBlur={() => {
                                        if (Number(p.quantity) < 1) {
                                            const temp = [...free_gifts];
                                            temp[index].quantity = 1;
                                            handleInputChange(selectedTab, "free_gifts", temp);
                                        }
                                    }}
                                    onChange={(val) => {
                                        handleQuantity(p.id, +val);
                                    }}
                                />

                                <div
                                    onClick={() => {
                                        removeProducts(p.id);
                                    }}
                                    className={styles.closeIcon}
                                >
                                    <CloseIcon />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div>
                {free_gift_template === "modern" && (
                    <InlineStack align="space-between" blockAlign="center">
                        <Text as="span" variant="bodyMd" fontWeight="regular">
                            Free Gift Text
                        </Text>
                        <Popover
                            active={textVariablePopoverActive}
                            activator={
                                <div
                                    onClick={() => setTextVariablePopoverActive((v) => !v)}
                                    style={{ cursor: "pointer", display: "flex" }}
                                >
                                    <Tooltip content="Add variable">
                                        <PiBracketsCurlyBold />
                                    </Tooltip>
                                </div>
                            }
                            onClose={() => setTextVariablePopoverActive(false)}
                        >
                            <ActionList
                                items={[
                                    {
                                        content: "Product title",
                                        onAction: () => handleVariableInsert("{product_title}"),
                                    },
                                ]}
                            />
                        </Popover>
                    </InlineStack>
                )}
                <FormTextField
                    name={`volume_discounts.${selectedTab}.free_gift_text`}
                    label={"Free Gift Text"}
                    labelHidden={free_gift_template === "modern"}
                    placeholder={free_gift_template === "modern" ? "e.g. + Free {product_title}" : "Free Gift Text"}
                    value={free_gift_text}
                    autoComplete={"off"}
                    onChange={(value) => handleInputChange(selectedTab, "free_gift_text", value)}
                    onBlur={() => {
                        if (!free_gift_text) {
                            handleInputChange(selectedTab, "free_gift_text", "+Free Gift");
                        }
                    }}
                    maxLength={50}
                    showCharacterCount
                />
            </div>

            <FormCheckbox
                name={`volume_discounts.${selectedTab}.free_gift_show_price`}
                label={"Show Prices"}
                checked={free_gift_show_price}
                onChange={(val) => handleInputChange(selectedTab, "free_gift_show_price", val)}
            />
        </div>
    );
};

export default VolumeFreeGiftForm;
